import { BonusClassification } from '@synergy/models';
import { EditIcon } from '@synergy/theme';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormHelperText, Grid, IconButton, Stack, Theme, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { AutocompleteOption, PoolAutocomplete } from '@synergy/components-next';
import { PoolTextArea, PoolTypography } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';

import { DeliveryTermsModal } from './terms-modal/delivery-terms-modal';
import { SupplierConditionsFormValues } from '../models/supplier-conditions-form-values';
import { supplierConditionsSelector } from '../store/supplier-conditions-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    term: {
        display: 'flex',
        alignItems: 'flex-start',

        '& > div:first-child': {
            flexGrow: 1,
            marginRight: theme.spacing(2),
        },
    },
}));

export const SupplierDetailConditionsPurchaseTerms = () => {
    const classes = useStyles();
    const theme = useTheme();
    const intl = useIntl();
    const supplierConditions = useAppSelector(supplierConditionsSelector);
    const { values } = useFormikContext<SupplierConditionsFormValues>();
    const [isDeliveryTermsModalVisible, setIsDeliveryTermsModalVisible] = useState(false);

    const bonusClassification = values.bonusClassification;

    const placeholderLabel =
        bonusClassification === BonusClassification.Contract || bonusClassification === BonusClassification.BonusPlus
            ? 'contract'
            : 'default';
    const conditionsOfPurchasePlaceholder = intl.formatMessage({
        id: `app.contactsAndOrganizations.detail.conditionsOfPurchase.placeholder.${placeholderLabel}`,
    });

    // Several hundred terms, many of them one supplier's own: a plain list had
    // to be scrolled through to find one. Typing a word of it narrows the list.
    const deliveryTermOptions: AutocompleteOption<number>[] = supplierConditions.deliveryTerms.map(deliveryTerm => ({
        value: deliveryTerm.id,
        displayValue: deliveryTerm.value,
    }));

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={12}>
                <PoolTypography styleType="sectionTitleNew" id="app.contactsAndOrganizations.detail.purchaseTerms" />
            </Grid>

            <Grid item={true} xs={12} className={classes.term}>
                <Field name="deliveryTermId">
                    {({ field, form, meta }: FieldProps<number>) => (
                        <Stack>
                            <PoolAutocomplete<number>
                                options={deliveryTermOptions}
                                value={field.value}
                                onChange={id => form.setFieldValue(field.name, id)}
                                label={<FormattedMessage id="app.contactsAndOrganizations.detail.deliveryTerm" />}
                                // Not mandatory: 1.978 of 3.434 suppliers carry
                                // none, and the GF decided against it (#2212, R-28).
                                required={false}
                                error={!!meta.touched && !!meta.error}
                            />
                            {meta.error && meta.touched && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </Stack>
                    )}
                </Field>
                <IconButton onClick={() => setIsDeliveryTermsModalVisible(true)} sx={{ mt: 0.75 }}>
                    <EditIcon htmlColor={theme.palette.primary.main} />
                </IconButton>
            </Grid>

            <Grid item={true} xs={12}>
                <Field name="conditionsOfPurchase">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextArea
                            {...field}
                            fullWidth={true}
                            rows="2"
                            rowsMax="50"
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.conditionsOfPurchase" />}
                            placeholder={conditionsOfPurchasePlaceholder}
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                        />
                    )}
                </Field>
            </Grid>

            <DeliveryTermsModal
                isVisible={isDeliveryTermsModalVisible}
                onClose={() => setIsDeliveryTermsModalVisible(false)}
            />
        </Grid>
    );
};
